"use client";

import { useSyncExternalStore } from "react";

/**
 * Quelle place le panneau d'étape prend sur le roll, pour ceux qui doivent
 * l'éviter.
 *
 * # Pourquoi ça ne se devine pas
 *
 * Le panneau change de forme d'une étape à l'autre : colonne de 400 px sur le
 * bord droit dans le parcours harmonique, bande en travers du haut pendant
 * qu'on joue, capsule centrée quand on ne joue pas. Un voisin qui voudrait
 * s'en écarter avec une constante se cognerait donc une fois sur deux, et
 * l'invitation à donner un retour l'a fait : centrée sur le conteneur entier,
 * elle mordait de 50 px sur la colonne.
 *
 * # Pourquoi un magasin externe et pas un contexte
 *
 * Publier depuis le panneau vers un état React de son parent demanderait un
 * `setState` dans un effet, c'est-à-dire un rendu en cascade à chaque étape —
 * et la règle que le projet applique ailleurs l'interdit. Écrire dans un
 * magasin externe est précisément ce à quoi sert un effet, et
 * `useSyncExternalStore` rend la lecture concurrente-sûre côté consommateur.
 * C'est aussi le motif que le Studio emploie déjà pour ses observables.
 *
 * Un seul parcours d'apprentissage est monté à la fois, d'où le magasin de
 * module. Le panneau publie `null` en partant, sinon la valeur survivrait au
 * démontage et le prochain voisin s'écarterait d'un fantôme.
 */
export type ShellOccupancy =
  /**
   * Une colonne sur le bord **droit**, sur toute la hauteur : un voisin qui
   * se centre le fait sur la bande restante, à gauche.
   */
  | { readonly kind: "column"; readonly width: number }
  /**
   * Une bande en travers du haut du roll. Elle n'occupe aucune colonne : un
   * voisin qui voulait s'écarter latéralement n'a plus de côté à éviter, il
   * a une hauteur.
   */
  | { readonly kind: "band" }
  /**
   * Une capsule centrée en haut du roll, large de `width`. Elle laisse les
   * deux bords libres et le bas du roll aussi, tant qu'elle reste courte.
   */
  | { readonly kind: "centered"; readonly width: number };

let current: ShellOccupancy | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function sameOccupancy(
  a: ShellOccupancy | null,
  b: ShellOccupancy | null,
): boolean {
  if (a === b) {
    return true;
  }

  if (a === null || b === null || a.kind !== b.kind) {
    return false;
  }

  return a.kind === "band" || b.kind === "band" || a.width === b.width;
}

/** Appelé par `FloatingShell` depuis un effet, jamais pendant un rendu. */
export function publishShellOccupancy(next: ShellOccupancy | null): void {
  if (sameOccupancy(current, next)) {
    return;
  }

  current = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ShellOccupancy | null {
  return current;
}

/**
 * Rend `null` tant qu'aucun panneau n'est monté — et au rendu serveur, où il
 * n'y a de toute façon pas de géométrie à éviter.
 */
export function useShellOccupancy(): ShellOccupancy | null {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

/**
 * Largeur de la colonne que le panneau prend sur le bord droit du roll, en
 * px — zéro quand il n'en prend aucune (bande, capsule centrée, ou pas de
 * panneau). C'est la seule chose que les voisins du bas ont besoin de savoir
 * pour se centrer sur la bande libre.
 */
export function occupiedColumnWidth(occupancy: ShellOccupancy | null): number {
  return occupancy?.kind === "column" ? occupancy.width : 0;
}
